import { GetStaticProps } from 'next';
import Router from 'next/router';
import { useEffect } from 'react';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import bundleAPI, { BUNDLE_END_POINT } from 'src/api/bundle';
import Seo from 'src/components/atoms/seo';
import Main from 'src/components/templates/main';
import config from 'src/config';
import { getBundleUrl } from 'src/helpers/utils';
import IBundle from 'src/interfaces/bundle';
import { IProductDetail } from 'src/interfaces/product';
import { SWRConfig } from 'swr';

import CPDBundleDetail from '@components/templates/bundlesCpdDetail';
import { setAffiliateCookie } from '@helpers/cookieUtils';

type Params = {
  locale: string;
};

export async function getStaticPaths() {
  const resBundles = await bundleAPI.fetchBundles(`${BUNDLE_END_POINT.BUNDLES}?theme=CPD`);
  const pathsIdName =
    resBundles.data.items?.map((bundle: IBundle) => ({
      params: {
        idName: bundle.product?.id_name,
      },
    })) || [];

  const pathsId =
    resBundles.data.items?.map((bundle: IBundle) => ({
      params: {
        idName: `${bundle.id}`,
      },
    })) || [];

  return {
    paths: [...pathsIdName, ...pathsId],
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context as Params;
  const idName = context.params?.idName;
  const responseProductDetail = await bundleAPI.fetchBundle(encodeURIComponent(idName as string));
  return {
    props: {
      fallback: {
        CPD_BUNDLE_ID_NAME: idName,
        CPD_BUNDLE: responseProductDetail.data || {},
        status: responseProductDetail.status,
      },
      ...(await serverSideTranslations(locale, ['common'])),
    },
    revalidate: config.REVALIDATE_SHORT,
  };
};

const CPDBundle = ({ fallback }: any) => {
  useEffect(() => {
    const productDetail: IProductDetail = fallback.CPD_BUNDLE;
    if (fallback.status === 404) {
      Router.push('/not-found');
    } else if (fallback.status === 500) {
      throw new Error('cannot get course');
    } else if (!productDetail.is_cpd_theme) {
      Router.push(getBundleUrl(productDetail.id_name, productDetail.is_cpd_theme));
    }
  });

  useEffect(() => {
    setAffiliateCookie();
  }, []);

  return (
    <SWRConfig value={{ fallback }}>
      <Main scrollToTop hideHeaderOnScroll scrollBottomDesktop={170} scrollBottomMobile={100} hideDownloadApp>
        <Seo
          title={fallback.CPD_BUNDLE.seo_title}
          description={fallback.CPD_BUNDLE.seo_description}
          image_url={fallback.CPD_BUNDLE.seo_image_url}
        />
        <CPDBundleDetail />
      </Main>
    </SWRConfig>
  );
};

export default CPDBundle;
